import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, TrendingUp } from 'lucide-react';
import Modal from './ui/Modal';

interface ExpiredTrialModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature: string; // e.g., "upload documents", "use AI queries"
}

const ExpiredTrialModal: React.FC<ExpiredTrialModalProps> = ({
  isOpen,
  onClose,
  feature
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    navigate('/pricing');
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={
        <>
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0" />
          <span className="text-red-700 dark:text-red-300">Trial Expired</span>
        </>
      }
      footer={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2.5 bg-stone-100 hover:bg-stone-200 dark:bg-coral-500/10 dark:hover:bg-coral-500/20 border border-stone-200 dark:border-coral-500/30 text-stone-700 dark:text-foreground-muted rounded-lg transition-colors text-sm"
          >
            Maybe Later
          </button>
          <button
            onClick={handleUpgrade}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white rounded-lg transition-colors font-semibold text-sm"
          >
            <TrendingUp className="w-4 h-4" />
            View Pricing Plans
          </button>
        </>
      }
    >
        <div className="space-y-4">
          <p className="text-stone-700 dark:text-foreground-secondary text-center">
            Your free trial has ended. To {feature}, please upgrade to a paid plan.
          </p>

          {/* This block used to be headed "Continue with full access" and list
              "Advanced document analysis" and "Priority support" — both of which
              are Advanced-only. Essential downgrades the model from Sonnet to
              Haiku and loses the Expenses page (gated on trial-or-Advanced in
              api/expenses.py), none of which was disclosed anywhere. */}
          <div className="bg-coral-50 dark:bg-coral-500/10 border border-coral-200 dark:border-coral-500/20 rounded-lg p-4">
            <h3 className="font-semibold text-coral-800 dark:text-foreground-muted mb-2">What the paid plans give you:</h3>
            <ul className="space-y-2 text-sm text-stone-700 dark:text-foreground-secondary">
              <li className="flex items-start gap-2">
                <span className="text-coral-600 dark:text-coral-400 mt-0.5">✓</span>
                <span><b>Essential</b> — 20 documents and 50 AI queries a month, on the faster basic model.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-coral-600 dark:text-coral-400 mt-0.5">✓</span>
                <span><b>Advanced</b> — 75 documents, 100 AI queries, the advanced model, expenses, compliance checking, KSeF e-invoicing and priority support.</span>
              </li>
            </ul>
            <p className="text-xs text-stone-600 dark:text-foreground-muted mt-3">
              Your trial ran on the advanced model with expenses included — Advanced keeps both.
            </p>
          </div>
        </div>
    </Modal>
  );
};

export default ExpiredTrialModal;
